import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { startReminderRunner, stopReminderRunner } from '../lib/reminders';
import { setWidgetLoggedOut } from '../lib/widget';
import { pushWidgetForUser } from '../lib/widgetSync';
import { onAppResume } from '../lib/native';
import { rescheduleHabitReminders, requestLocalNotificationPermission, localNotificationsStatus, drainPendingActions } from '../lib/localNotifications';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        startReminderRunner(session.user.id);
        pushWidgetForUser(session.user.id);
        // Schedule iOS / Android local notifications (no-op on web).
        rescheduleHabitReminders(session.user.id);
        // Drain any "Mark done" notification actions that fired while the
        // app was backgrounded.
        drainPendingActions().then((n) => {
          if (n > 0) pushWidgetForUser(session.user.id);
        });
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        startReminderRunner(newSession.user.id);
        pushWidgetForUser(newSession.user.id);
        rescheduleHabitReminders(newSession.user.id);
        // First-time sign-in on native: auto-prompt for notification
        // permission ONLY if the current state is "prompt" (never asked
        // before). If the user previously denied, iOS won't re-prompt and
        // we'd just silently get the cached denial — better to let them
        // discover the Settings → Habit reminders flow.
        if (event === 'SIGNED_IN') {
          (async () => {
            try {
              const status = await localNotificationsStatus();
              if (status.supported && status.canPrompt) {
                const res = await requestLocalNotificationPermission();
                if (res.granted) rescheduleHabitReminders(newSession.user.id);
              }
            } catch { /* no-op */ }
          })();
        }
      } else {
        stopReminderRunner();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Subscribe to habit table changes so iOS / Android local notifications
  // stay in sync without having to inject reschedule calls into every CRUD
  // site (Today, Habits, HabitDetail, Welcome, Templates, Settings).
  useEffect(() => {
    if (!user?.id) return undefined;
    let debounceId = null;
    const trigger = () => {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(() => rescheduleHabitReminders(user.id), 600);
    };
    const channel = supabase
      .channel(`habits-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'habits', filter: `user_id=eq.${user.id}` },
        trigger,
      )
      .subscribe();
    return () => {
      if (debounceId) clearTimeout(debounceId);
      try { supabase.removeChannel(channel); } catch { /* no-op */ }
    };
  }, [user?.id]);
  useEffect(() => {
    let cleanup = () => {};
    (async () => {
      cleanup = await onAppResume(() => {
        if (user?.id) {
          pushWidgetForUser(user.id);
          rescheduleHabitReminders(user.id);
          drainPendingActions().then((n) => {
            if (n > 0) pushWidgetForUser(user.id);
          });
        }
      });
    })();
    return () => cleanup();
  }, [user?.id]);

  const signUp = async (email, password, displayName) => {
    const redirectTo = `${window.location.origin}/verify-email`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: { display_name: displayName },
      },
    });
    return { data, error };
  };

  const signIn = async (email, password) => {
    return supabase.auth.signInWithPassword({ email, password });
  };

  const signOut = async () => {
    stopReminderRunner();
    await setWidgetLoggedOut();
    await supabase.auth.signOut();
  };

  const forgotPassword = async (email) => {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
  };

  const updatePassword = async (password) => {
    return supabase.auth.updateUser({ password });
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signUp, signIn, signOut, forgotPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
